import {useEffect, useCallback} from 'react';
import {handleMultiLinePaste, isLastEditableElement, isFirstEditableElement, focusPreviousEditableElement, focusNextEditableElement, splitContentAtCursor, getCursorOffsetInElement, getSelectionEndOffsetInElement, createRangeAtOffset, getTextContent} from '../utils/dom';

type refType = HTMLDivElement | HTMLSpanElement;
interface UseParagraphEditorProps {
    content: string;
    contentEditableRef: React.RefObject<refType>;
    onChange?: (content: string) => void;
    onFocus?: () => void;
    onEditableFocus?: (element: HTMLElement | null) => void;
    onEditableCursorMove?: () => void;
    onEnterKeySplit?: () => boolean;
    onEnterKey?: () => void;
    onBackspaceKeyMerge?: () => boolean;
    enableMultiLinePaste?: boolean;
}
export const EMPTY_PARAGRAPH = '--';
export function useParagraphEditor({
    content,
    contentEditableRef,
    onChange,
    onFocus,
    onEditableFocus,
    onEditableCursorMove,
    onEnterKeySplit,
    onEnterKey,
    onBackspaceKeyMerge,
    enableMultiLinePaste = false
}: UseParagraphEditorProps) {
    useEffect(() => {
        if(contentEditableRef.current) {
            const currentHtml = contentEditableRef.current.innerHTML || '';
            if(currentHtml !== content) {
                if(document.activeElement !== contentEditableRef.current) {
                    contentEditableRef.current.innerHTML = content;
                }
            }
        }
    }, [content, contentEditableRef]);

    const handleInput = useCallback((e: React.FormEvent<refType>) => { 
        const newHtmlContent = e.currentTarget.innerHTML || '';
        onChange?.(newHtmlContent);
    }, [onChange]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<refType>) => {
        if(e.key === 'Backspace' && contentEditableRef.current) {
            const selection = window.getSelection();
            // Only navigate if no text is selected
            if(selection?.isCollapsed) {
                const cursorOffset = getCursorOffsetInElement(contentEditableRef.current);
                if(cursorOffset === 0 && !isFirstEditableElement(contentEditableRef.current)) {
                    e.preventDefault();
                    
                    // Try to merge with previous row
                    const merged = onBackspaceKeyMerge?.();
                    if(!merged) {
                        // Fallback to navigation if merge failed
                        focusPreviousEditableElement(contentEditableRef.current);
                    }
                    return;
                }
            }
        }
        
        if(e.key === 'Delete' && contentEditableRef.current) {
            const selection = window.getSelection();
            // Only navigate if no text is selected
            if(selection?.isCollapsed) {
                const content = getTextContent(contentEditableRef.current.innerHTML);
                const cursorOffset = getCursorOffsetInElement(contentEditableRef.current);
                if(cursorOffset === content.length && !isLastEditableElement(contentEditableRef.current)) {
                    e.preventDefault();
                    focusNextEditableElement(contentEditableRef.current);
                    return;
                }
            }
        }
        
        if(e.key === 'Enter') {
            e.preventDefault();
            
            // Check if this is the last editable element - do nothing if so
            if(contentEditableRef.current && isLastEditableElement(contentEditableRef.current)) {
                return;
            }
            
            const handled = onEnterKeySplit?.();
            if(!handled) {
                onEnterKey?.();
            }
        }
    }, [onEnterKeySplit, onEnterKey, onBackspaceKeyMerge]);

    const handlePaste = useCallback((e: React.ClipboardEvent<refType>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        
        if(!contentEditableRef.current) return;
        
        const selection = window.getSelection();
        if(!selection || !selection.rangeCount) return;
        
        if(enableMultiLinePaste && text.includes('\n')) {
            handleMultiLinePaste(contentEditableRef.current, text, onChange);
        } else {
            // Single line paste - preserve variables
            const singleLineText = text.replace(/\n/g, ' ');
            const startOffset = getCursorOffsetInElement(contentEditableRef.current);
            const endOffset = getSelectionEndOffsetInElement(contentEditableRef.current);
            const {contentBefore} = splitContentAtCursor(contentEditableRef.current, startOffset);
            const {contentAfter} = splitContentAtCursor(contentEditableRef.current, endOffset);
            
            // Rebuild content with pasted text replacing any selected text
            contentEditableRef.current.innerHTML = contentBefore + singleLineText + contentAfter;
            
            // Set cursor after pasted text
            const newCursorOffset = startOffset + singleLineText.length;
            const newRange = createRangeAtOffset(contentEditableRef.current, newCursorOffset);
            const newSelection = window.getSelection();
            if(newSelection) {
                newSelection.removeAllRanges();
                newSelection.addRange(newRange);
            }
            
            // Trigger onChange
            onChange?.(contentEditableRef.current.innerHTML);
        }
    }, [enableMultiLinePaste, onChange]);

    const handleBlur = useCallback((e: React.FocusEvent<refType>) => {
        const currentContent = e.currentTarget.innerHTML || '';
        const textContent = getTextContent(currentContent);
        const trimmedText = textContent.trim();
        if(trimmedText === '' && contentEditableRef.current) {
            onChange?.(EMPTY_PARAGRAPH);
        }
    }, [onChange]);

    const handleFocus = useCallback(() => {
        // Clear the placeholder if it's showing
        if(contentEditableRef.current) {
            const currentHtml = contentEditableRef.current.innerHTML || '';
            const textContent = getTextContent(currentHtml);
            
            if(textContent.trim() === EMPTY_PARAGRAPH) {
                contentEditableRef.current.innerHTML = '';
                onChange?.(''); // Sync parent state
            }
        }
        
        onFocus?.();
        onEditableFocus?.(contentEditableRef.current ?? null);
    }, [onChange, onFocus, onEditableFocus]);

    const handleCursorMove = useCallback(() => {
        onEditableCursorMove?.();
    }, [onEditableCursorMove]);

    return {
        handleInput,
        handleKeyDown,
        handlePaste,
        handleBlur,
        handleFocus,
        handleCursorMove
    };
}
